'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { mutate as globalMutate } from 'swr';
import useSWRMutation from 'swr/mutation';
import customFetch from '@/lib/utils/fetch';
import type { LiveAccount } from '@/lib/live-trading/types';

const ACCOUNT_KEY = 'api:/live-trading/account';

export interface UseInitSystemWalletOptions {
  /** init 返回 initializing（HTTP 202）后是否轮询 account 直到 active，默认开启 */
  pollUntilActive?: boolean;
  /** 轮询间隔（ms），默认 3000 */
  pollInterval?: number;
  /** 最大轮询次数，默认 20（约 1 分钟）；超时后返回最后一次的账户状态 */
  maxPollAttempts?: number;
}

async function fetchAccount(): Promise<LiveAccount | null> {
  const res = await customFetch(`/api/live-trading/account`);
  if (!(res instanceof Response) || !res.ok) return null;
  return res.json();
}

/**
 * init 接口响应：已有账户时直接返回账户本体（200，含地址/状态），
 * 新开户时为 202 { status: 'initializing' }。
 */
export type InitSystemWalletResponse = LiveAccount | { status: 'initializing' };

/** 是否为账户本体（区别于 202 的 { status } 占位响应） */
function isLiveAccount(res: InitSystemWalletResponse): res is LiveAccount {
  return 'user_id' in res;
}

/**
 * 开通实盘托管账户（系统钱包）。
 * POST /api/live-trading/account/init：已有账户直接返回账户本体（200）；
 * 未开通则异步幂等开户，202 → { status: 'initializing' }。
 * 开启轮询时（默认），init() 会循环查询 GET /api/live-trading/account，
 * status=active（开通成功）或 failed 时终止并返回最终账户；关闭轮询则发完 init 即返回。
 * 期间同步作废 SWR 缓存，useLiveAccount / TrackingWalletsProvider 的 systemWallet 随之更新。
 */
export function useInitSystemWallet(options: UseInitSystemWalletOptions = {}) {
  const { pollUntilActive = true, pollInterval = 3_000, maxPollAttempts = 20 } = options;

  const { trigger, isMutating, error } = useSWRMutation<InitSystemWalletResponse>(
    'api:/live-trading/account/init',
  );
  const [isPolling, setIsPolling] = useState(false);
  // 卸载后停止轮询，避免对已卸载组件 setState
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /**
   * 发起开户。返回最终账户状态（未开启轮询或无需轮询时返回 null）。
   * 调用方通过返回值的 status 判断结果：active=成功，failed=失败，
   * initializing=轮询超时仍在开通中。
   */
  const init = useCallback(async (): Promise<LiveAccount | null> => {
    const res = await trigger({ method: 'POST' });
    await globalMutate(ACCOUNT_KEY);

    // 已有账户的终态（active/failed/pending_init）直接返回，无需轮询
    if (res && isLiveAccount(res) && res.status !== 'initializing') return res;
    // initializing（新开户 202，或已有账户仍在开通中）→ 按需轮询
    if (!pollUntilActive || res?.status !== 'initializing') return null;

    setIsPolling(true);
    try {
      let account: LiveAccount | null = null;
      for (let i = 0; i < maxPollAttempts && mountedRef.current; i++) {
        await new Promise((resolve) => setTimeout(resolve, pollInterval));
        account = await fetchAccount();
        if (!account) continue;
        if (account.status === 'active' || account.status === 'failed') {
          // 终态：同步 SWR 缓存后终止轮询
          await globalMutate(ACCOUNT_KEY, account, { revalidate: false });
          return account;
        }
      }
      return account;
    } finally {
      if (mountedRef.current) setIsPolling(false);
    }
  }, [trigger, pollUntilActive, pollInterval, maxPollAttempts]);

  return {
    init,
    /** POST init 请求进行中 */
    isInitializing: isMutating,
    /** 轮询账户状态中 */
    isPolling,
    /** init 请求 + 轮询全程 */
    isOpening: isMutating || isPolling,
    error,
  };
}

export default useInitSystemWallet;
